import { documentService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IInspectionAttempt,
  IUploadedFile,
  InspectionActionTypes,
  IssueType,
} from "@miplatform/types";
import {
  createUUID,
  externalReferenceId,
  getDocumentTypeByContentType,
} from "@miplatform/utils";
import { useInspectionAttempt } from "@mibuildweb/hooks";
import { useInspectionsContext } from "../../InspectionsProvider";
import { IInspectionFormIssue } from "./inspectionFormSchemas";

export const useInspectionDrawerViewModel = ({
  onClose,
  setDrawerType,
}: {
  onClose: (refresh?: boolean) => void;
  setDrawerType: (type: InspectionActionTypes | null) => void;
}) => {
  const stateManager = useComponentState();
  const {
    greenTagInspectionAttempt,
    redTagInspectionAttempt,
    scheduleInspectionAttempt,
    isLoading: inspectionAttemptLoading,
  } = useInspectionAttempt();

  const {
    dateRange,
    jumpToInspection,
    refreshInspections,
    setShowHighlight,
    getJumpRange,
    getInspectionAttemptDetails,
  } = useInspectionsContext();

  const handleScheduleSubmit = async ({
    attemptId,
    scheduledDate,
  }: {
    attemptId: string;
    scheduledDate: string;
  }) => {
    const jumpRange = getJumpRange(scheduledDate, dateRange);

    const buttonProps = jumpRange
      ? {
          title: "Jump to Inspection",
          onClick: () => {
            jumpToInspection(jumpRange);
          },
        }
      : null;

    const response = await scheduleInspectionAttempt({
      attemptId,
      scheduledDate,
      buttonProps,
    });

    setShowHighlight(true);

    if (response.status >= HTTP_STATUSES.OK) {
      onClose(true);
    }
  };

  const handleGreenTagSubmit = async ({
    id,
    inspectedDate,
    reportedDate,
    files = [],
    externalReferenceId,
  }: {
    id: string;
    inspectedDate?: string;
    reportedDate?: string;
    files?: IUploadedFile[];
    externalReferenceId: string;
  }) => {
    const response = await greenTagInspectionAttempt({
      id,
      inspectedDate,
      reportedDate,
    });
    if (response.status >= HTTP_STATUSES.OK) {
      if (files.length > 0) {
        uploadImages({
          images: files,
          externalReferenceId,
        });
      }
      onClose(true);
    }
  };

  const handleRedTagSubmit = async ({
    id,
    inspectedDate,
    reportedDate,
    issues = [],
    files = [],
    scheduleNextInspection = false,
    assignmentId,
    externalReferenceId,
  }: {
    id: string;
    inspectedDate?: string;
    reportedDate?: string;
    issues?: IInspectionFormIssue[];
    files?: IUploadedFile[];
    scheduleNextInspection?: boolean;
    assignmentId: string;
    externalReferenceId?: string;
  }) => {
    const response = await redTagInspectionAttempt({
      id,
      inspectedDate,
      reportedDate,
    });

    if (response.status >= HTTP_STATUSES.OK) {
      if (files.length > 0) {
        if (!externalReferenceId) {
          toastService.error({
            text: "Missing externalReferenceId for image upload.",
          });
        } else {
          uploadImages({
            images: files,
            externalReferenceId,
          });
        }
      }
      if (issues.length > 0) {
        if (!assignmentId) {
          toastService.error({
            text: "Missing assignmentId for issue creation.",
          });
        } else {
          issues.forEach((issue) => {
            submitIssue(issue, assignmentId);
          });
        }
      }

      if (scheduleNextInspection) {
        handleScheduleNextAttempt(response.data);
      } else {
        onClose(true);
      }
    }
  };

  const handleScheduleNextAttempt = async (
    inspectionAttempt: IInspectionAttempt
  ) => {
    const { nextAttemptIds } = inspectionAttempt;
    if (!Array.isArray(nextAttemptIds) || nextAttemptIds.length === 0) {
      toastService.error({
        text: "No next inspection attempt ID found.",
      });
      onClose(true);
      return;
    }

    const [newAttemptId] = nextAttemptIds;

    await getInspectionAttemptDetails(newAttemptId);
    setDrawerType(InspectionActionTypes.Schedule);
    refreshInspections();
  };

  const uploadImages = async ({
    images,
    externalReferenceId,
  }: {
    images: IUploadedFile[];
    externalReferenceId: string;
  }) => {
    const documentResponses = await Promise.all(
      images.map((file) => {
        const params = {
          title: file.name,
          externalReferenceId,
          documentType: getDocumentTypeByContentType(file.type),
        };
        return documentService.documents.create({
          data: file,
          params,
          contentType: file.type,
        });
      })
    );

    return documentResponses;
  };

  const submitIssue = (issue: IInspectionFormIssue, assignmentId: string) => {
    stateManager.run(async () => {
      const id = createUUID();

      await taskService.issues.create({
        data: {
          assignmentId,
          id,
          title: issue.title,
          originalDescription: issue.description,
          description: issue.description,
          type: IssueType.Recordable,
          causedByTradeId: issue.causedByTradeId,
          resolvedByTradeId: issue.resolvedByTradeId,
        },
        toastMessageConfig: {
          failure: {
            text: `There was an error creating the issue ${issue.title}.`,
          },
        },
      });

      if (issue.files && issue.files.length > 0) {
        uploadImages({
          images: issue.files,
          externalReferenceId: externalReferenceId.getIssueRefId({
            issueId: id,
          }),
        });
      }
    });
  };

  return {
    handleGreenTagSubmit,
    handleRedTagSubmit,
    handleScheduleSubmit,
    isLoading: stateManager.isBusy() || inspectionAttemptLoading,
  };
};
